/**
 * Models V2 API client — the family + Auto(best-in-family) + live-model-list
 * tier editor (behind VITE_MODELS_V2).
 *
 * Three surfaces, all already shipped server-side (commit 2b1aa9a):
 *   - BASE   GET  /api/settings/provider-tiers            → rows[{provider, top, balanced, cheap, …}]
 *            PATCH /api/settings/provider-tiers/:provider  → write the global base
 *   - OVERRIDE GET    /api/orgs/:orgId/provider-tier-overrides
 *              PUT    /api/orgs/:orgId/provider-tier-overrides/:provider
 *              DELETE /api/orgs/:orgId/provider-tier-overrides/:provider
 *   - LIVE   GET  /api/settings/models/available?provider=… (ordinary cached read)
 *            POST /api/settings/models/available?provider=… (forced live refresh)
 *
 * Paths are written WITHOUT the `/api` prefix — `apiJson` prepends
 * env.VITE_API_BASE_URL (`/api`), matching the all-orgs-settings client idiom.
 *
 * Every tier facet on a row is {family, mode, pinnedModel, model, resolvedModel}.
 * `resolvedModel` is what Auto currently picks (computed server-side from the
 * in-memory live cache + hardcoded family fallback) so the UI can show
 * "Auto → claude-opus-4-8" without resolving anything client-side.
 */
import { apiJson } from './client';

/** Internal provider keys. 'gemini' is accepted as an alias for 'google' on the
 *  live-models endpoint, but the tier rows use the stored provider key. */
export type ModelProvider = string; // 'anthropic' | 'openai' | 'google' (+ future)

/** The label/intent order top → balanced → cheap. The DB column for "cheap" is
 *  still `fast_tier_model` (UI-only rename); the API row already exposes it as
 *  the `cheap` facet object. */
export type TierKey = 'top' | 'balanced' | 'cheap';
export const TIER_KEYS: readonly TierKey[] = ['top', 'balanced', 'cheap'] as const;
export const TIER_LABEL: Record<TierKey, string> = {
  top: 'Top',
  balanced: 'Balanced',
  cheap: 'Cheap',
};

export type TierMode = 'auto' | 'pinned';

/** One tier facet as returned by the gateway. */
export interface TierFacet {
  family: string | null;
  mode: TierMode;
  pinnedModel: string | null;
  /** legacy effective *_tier_model column (back-compat) */
  model: string | null;
  /** what Auto resolves to RIGHT NOW (server-computed; never blank for a valid row) */
  resolvedModel: string | null;
  /** WHERE resolvedModel came from. 'subscription' = the newest model in the
   *  signed-in subscription's own catalogue (the list the card pickers show).
   *  'live' = the newest model the provider API lists in this family (the
   *  subscription catalogue was not known). 'fallback' = neither list was
   *  available and this is a hardcoded built-in constant — it is NOT a claim
   *  about what is newest, and the UI must label it as such. */
  resolvedFrom?: TierResolutionSource;
  /** effective endpoint only (2026-10-05): which setting decides this tier —
   *  'workspace' when the Workspace overrides at least one facet of it,
   *  'account' when it inherits the base. */
  layer?: 'workspace' | 'account';
}

/** Provenance of a resolved tier model (mirrors the gateway's
 *  lib/model-families.ts TierResolutionSource). */
export type TierResolutionSource =
  | 'pinned'
  | 'subscription'
  | 'live'
  | 'fallback'
  | 'legacy'
  | 'none';

/** A provider row (base or effective). */
export interface ProviderTierRowV2 {
  provider: ModelProvider;
  topTierModel: string | null;
  balancedTierModel: string | null;
  fastTierModel: string | null;
  top: TierFacet;
  balanced: TierFacet;
  cheap: TierFacet;
  updatedAt?: string | null;
  /** effective endpoint only: true when this org has an override row */
  hasOverride?: boolean;
}

/** A live-list model entry. */
export interface AvailableModel {
  id: string;
  displayName: string;
  family: string | null;
  created?: number | null;
  deprecated?: boolean;
  /** Native Claude alias rows only (gateway T-69, 2026-10-04): the versioned
   *  model the alias runs today. The row's `id` stays the alias (`opus`);
   *  this is what its label must name ("Opus 5.5"). */
  resolvedModelId?: string;
  resolvedDisplayName?: string;
  /** The vendor's own account-default model (codex model/list `isDefault`). */
  vendorDefault?: boolean;
}

/** Where the live-list came from — drives the "live as of HH:MM" vs
 *  "cached/last-known" indicator. */
export type ModelListSource = 'live' | 'native-interactive' | 'account-cache' | 'cached' | 'fallback';
export type ModelCatalogueLane = 'provider-api' | 'codex-subscription' | 'claude-subscription';

export interface AvailableModelsResponse {
  provider: string;
  models: AvailableModel[];
  /** explicit provenance: provider "live", Codex "account-cache", local TTL
   *  "cached", or "fallback" after catalogue failure. */
  source?: ModelListSource;
  /** Which actual execution credential lane the models belong to. */
  catalogueLane?: ModelCatalogueLane;
  /** CLI label observed on the agent image for subscription catalogues. */
  runtimeVersion?: string | null;
  /** legacy: === (source === 'fallback'). */
  stale: boolean;
  note?: string | null;
  /** machine-readable failure reason when source === 'fallback'. */
  errorCode?: ModelListErrorCode | null;
  /** present ONLY on a genuine live fetch — what actually changed vs the
   *  previously-known list. Drives the honest Refresh report. */
  changed?: ModelListDiff | null;
  /** epoch ms the underlying live list was fetched (0/absent if never). */
  fetchedAt?: number | null;
}

/** Why a list is a fallback (mirrors the gateway's ModelListErrorCode). */
export type ModelListErrorCode =
  | 'no_credential'
  | 'subscription_catalogue_missing'
  | 'subscription_catalogue_invalid'
  | 'auth_expired'
  | 'auth_rejected'
  | 'http_error'
  | 'network_error';

/** What a live fetch changed versus the previously-known list. */
export interface ModelListDiff {
  added: string[];
  removed: string[];
  unchanged: number;
  firstFetch: boolean;
}

/** PATCH body for the base (only the facets the user touched). camelCase keys
 *  map to the gateway's per-tier facet fields. The gateway uses `fast*` keys
 *  for the Cheap tier (DB column name preserved). */
export interface PatchProviderTierInput {
  topTierFamily?: string;
  topTierMode?: TierMode;
  topTierPinnedModel?: string;
  balancedTierFamily?: string;
  balancedTierMode?: TierMode;
  balancedTierPinnedModel?: string;
  fastTierFamily?: string;
  fastTierMode?: TierMode;
  fastTierPinnedModel?: string;
}

/** PUT body for a per-org override. Same facet keys; null clears a facet
 *  (inherit base), omit keeps it unchanged. */
export type UpsertProviderTierOverrideInput = {
  [K in keyof PatchProviderTierInput]?: PatchProviderTierInput[K] | null;
};

/** Map a UI TierKey → the gateway facet-key prefix (Cheap → `fast`). */
export function tierKeyToApiPrefix(tier: TierKey): 'top' | 'balanced' | 'fast' {
  return tier === 'cheap' ? 'fast' : tier;
}

/** The list of family choices the picker offers per provider (mirrors the
 *  gateway FAMILIES_BY_PROVIDER taxonomy). Falls back to "infer from live list"
 *  for an unknown provider. */
export const FAMILIES_BY_PROVIDER: Record<string, readonly string[]> = {
  anthropic: ['opus', 'sonnet', 'fable', 'haiku'],
  openai: ['gpt-flagship', 'gpt-balanced', 'gpt-cheap'],
  google: ['pro', 'flash', 'cheap'],
};

export const PROVIDER_DISPLAY_NAME: Record<string, string> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  google: 'Gemini',
  gemini: 'Gemini',
};

export function providerDisplayName(provider: string): string {
  return PROVIDER_DISPLAY_NAME[provider] ?? provider;
}

export function familyLabel(family: string): string {
  // Human-friendly family labels; unknown families render verbatim.
  switch (family) {
    case 'opus':
      return 'Opus';
    case 'sonnet':
      return 'Sonnet';
    case 'haiku':
      return 'Haiku';
    case 'fable':
      return 'Fable';
    case 'gpt-flagship':
      return 'GPT flagship';
    case 'gpt-balanced':
      return 'GPT balanced';
    case 'gpt-cheap':
      // 2026-10-09: the cheap tier is Luna now, not only -mini/-nano ids
      // (gateway lib/model-families.ts OPENAI_TIER_WORDS).
      return 'GPT cheap';
    case 'pro':
      return 'Pro';
    case 'flash':
      return 'Flash';
    case 'cheap':
      return 'Flash-Lite';
    default:
      return family;
  }
}

export const modelsV2Api = {
  // ── BASE (account-global provider_tiers) ─────────────────────────────
  listBase(): Promise<{ rows: ProviderTierRowV2[]; note?: string }> {
    return apiJson<{ rows: ProviderTierRowV2[]; note?: string }>('/settings/provider-tiers');
  },
  patchBase(provider: string, input: PatchProviderTierInput): Promise<{ row: ProviderTierRowV2 }> {
    return apiJson<{ row: ProviderTierRowV2 }>(
      `/settings/provider-tiers/${encodeURIComponent(provider)}`,
      { method: 'PATCH', json: input },
    );
  },

  // ── LIVE per-provider model list (account-owner gated) ───────────────
  available(provider: string, refresh = false): Promise<AvailableModelsResponse> {
    const usp = new URLSearchParams({ provider });
    if (refresh) {
      // Forced native discovery may outlive a service worker that still controls
      // a returning tab. All shipped workers ignore non-GET requests, so this
      // read-only POST reaches the gateway instead of letting an old worker turn
      // a later 200 into its obsolete synthetic timeout 503.
      return apiJson<AvailableModelsResponse>(
        `/settings/models/available?${usp.toString()}`,
        { method: 'POST' },
      );
    }
    return apiJson<AvailableModelsResponse>(`/settings/models/available?${usp.toString()}`);
  },

  // ── PER-ORG OVERRIDE ─────────────────────────────────────────────────
  listOverrides(orgId: string): Promise<{ overrides: ProviderTierRowV2[] }> {
    return apiJson<{ overrides: ProviderTierRowV2[] }>(
      `/orgs/${encodeURIComponent(orgId)}/provider-tier-overrides`,
    );
  },
  putOverride(
    orgId: string,
    provider: string,
    input: UpsertProviderTierOverrideInput,
  ): Promise<{ override: ProviderTierRowV2 }> {
    return apiJson<{ override: ProviderTierRowV2 }>(
      `/orgs/${encodeURIComponent(orgId)}/provider-tier-overrides/${encodeURIComponent(provider)}`,
      { method: 'PUT', json: input },
    );
  },
  deleteOverride(orgId: string, provider: string): Promise<{ ok: boolean }> {
    return apiJson<{ ok: boolean }>(
      `/orgs/${encodeURIComponent(orgId)}/provider-tier-overrides/${encodeURIComponent(provider)}`,
      { method: 'DELETE' },
    );
  },
};

export const modelsV2Keys = {
  all: ['models-v2'] as const,
  base: () => [...modelsV2Keys.all, 'base'] as const,
  available: (provider: string) => [...modelsV2Keys.all, 'available', provider] as const,
  overrides: (orgId: string) => [...modelsV2Keys.all, 'overrides', orgId] as const,
};
